/**
 * Processing files of FGDB, Shapefile...
 */

import * as JSZip from 'jszip';
import { RE } from './FileReverseEng';

export namespace FP {

	export interface FileNameParts {
		ext: string; // file extension
		nameNoExt: string; // file name without extension
	}
	export interface DirectoryFile {
		dirName: string;
		files: File[];
	}
	export interface GroupedFiles {
		txt?: {
			[fileName: string]: {
				file: File,
				zip?: Blob;
				binaryString?: string;
				headers?: any[];
				matchingColumns?: any[];
				previewData?: any;
			}
		};
		txtZip?: {
			[zipName: string]: {
				layers?: {
					[layerNameWithExtension: string]: {
						unzip: JSZip.JSZipObject,
						binaryString?: string;
						fileData?: string[];
						headers?: any[];
						matchingColumns?: any[];
						previewData?: any;
					}
				}
				zip: any;
			}
		};
		fgdb?: {
			[fgdbName: string]: {
				zip: any; // JSZip.JSZipObject | File;
				files: { // Grouped by name and extension // TODO
					[a0000000x: string]: {
						GDBTABLE?: File;
						GDBTABLX?: File;
					}
				};
				rawFiles: File[] | FileList; // Files uploaded directly by user (unfiltered)
				layers?: any; // TODO // Info which is read from files
			}
		};
		fgdbZip?: {
			[zipName: string]: {
				unzips: {
					[a0000000x: string]: {
						GDBTABLE?: JSZip;
						GDBTABLX?: JSZip;
					}
				};
				zip: File;
				layers?: any; // After processing // TODO
			};
		};
		shp?: {
			[fileName: string]: {
				files: {
					SHX: File,
					SHP: File,
					PRJ: File,
					DBF: File
				};
				info: any; // After processing // TODO
				zip: any;
			}
		};
		shpZip?: {
			[zipName: string]: {
				layers: {
					[layerName: string]: {
						unzips: {
							DBF?: JSZip;
							PRJ?: JSZip;
							SHP?: JSZip;
							SHX?: JSZip;
						};
						info: any; // After processing // TODO
					}
				};
				zip: File
			};
		};
	}
	export interface FileProcessOptions {
		previewNumShp?: number; // number of rows to read from dbf
	}

	export interface Spec {
		cus_id: string;
		files: FilesJsonSpatial[];
	}
	// For a file with multiple layers, such as fgdb, fgdbZip, shpZip
	export interface FilesJsonSpatial {
		name: string;
		layers: LayerJsonSpatial[];
		type: 'shapefile' | 'fgdb';
	}
	export interface FilesJsonCsv {
		name: string;
		layers: LayerJsonCsv[];
		type: 'csv';
	}
	// For a shapefile
	export interface LayerJsonSpatial {
		name: string;
		type: string; // 'PSAP',
		sequence: any; // Generated at backend
		lrs_cols?: any[]; // TODO: any
		attr_cols?: any[]; // TODO
		lrs?: {
			name: string;
			desc: string;
			meas_uom: string;
			meas_precision: number;
		};
		layer_spec: FileSpecFeatureFile;
		arnold?: 0 | 1; // No arnold in AZGIV
	}
	export interface LayerJsonCsv {
		name: string; // Layer name without extension
		originalName: string; // Layer name with extension
		type: string; // TODO,
		sequence: any; // Generated at backend
		lrs_cols?: any[]; // TODO: any
		attr_cols?: any[]; // TODO
		layer_spec: {
			delimiter: string;
		};
	}
	export interface FileSpecFeatureFile {
		// srid: string; // eg. '3857'
		proj4: string;
		geom_col: string;
	}

	export interface LayersByName {
		[name: string]: {
			fileData: string[],
			name: string
		};
	}

	export class FileUtility {
		// Get file name's extension, name without extentsion
		static getFileNameParts(fName: string): FileNameParts {
			// File name without extension
			const nameNoExt = fName.slice(0, fName.lastIndexOf('.'));
			const nameSplits = fName.split('.');
			let extension = nameSplits[nameSplits.length - 1];
			extension = extension ? extension.toUpperCase() : '';
			return {
				ext: extension, // converted to uppercase
				nameNoExt: nameNoExt
			};
		}
		// This is for cases that may involve folders (not only files)
		static getFilesFromDataTransferItems(items: DataTransferItem[] | DataTransferItemList,
		successCallback?, errorCallback?): Promise<(DirectoryFile | File)[]> {
			const entryPromises = [];
			for (let i = 0; i < items.length; i++) {
				const entry = items[i].webkitGetAsEntry();
				if (entry) {
					if (entry.isDirectory) {
						const entryPromise: Promise<DirectoryFile> = new Promise((resolveEntry, rejectEntry) => {
							const dirFiles = [];
							const dirReader = entry.createReader();
							// DO NOT use the method beblow due to Chrome issues. It will only read 100 entries in Chrome
							// The problem with this is that readEntries is asynchronous, so I'm trying to call it while it's "busy" reading the first batch.
							// dirReader.readEntries((entries) => {
							// 	const entryFilePromises = [];
							// 	entries.forEach((e) => {
							// 		const entryFilePromise = new Promise((resolveFile, rejectFile) => {
							// 			e.file((f) => {
							// 				dirFiles.push(f);
							// 				resolveFile();
							// 			}, () => {
							// 				rejectFile();
							// 			});
							// 		});
							// 		entryFilePromises.push(entryFilePromise);
							// 	});
							// 	Promise.all(entryFilePromises).then(() => {
							// 		resolveEntry({
							// 			dirName: entry.name,
							// 			files: dirFiles
							// 		});
							// 	})
							// 	.catch(() => {
							// 		rejectEntry();
							// 	});
							// });

							// This method will solve the 100 entries limit issue in chrome
							// Reference: https://stackoverflow.com/questions/51850469/file-and-directory-entries-api-broken-in-chrome
							const entryFilePromises = [];
							const read = () => {
								dirReader.readEntries((entries) => {
									if (entries.length > 0) {
										entries.forEach((e) => {
											const entryFilePromise = new Promise((resolveFile, rejectFile) => {
												e.file((f) => {
													dirFiles.push(f);
													resolveFile();
												}, () => {
													rejectFile();
												});
											});
											entryFilePromises.push(entryFilePromise);
										});
										read();
									} else {
										Promise.all(entryFilePromises).then(() => {
											resolveEntry({
												dirName: entry.name,
												files: dirFiles
											});
										})
										.catch(() => {
											rejectEntry();
										});
									}
								});
							};
							read();
						});
						entryPromises.push(entryPromise);
					} else if (entry.isFile) {
						const entryPromise: Promise<DirectoryFile> = new Promise((resolveEntry, rejectEntry) => {
							entry.file((f) => {
								resolveEntry(f);
							}, () => {
								rejectEntry();
							});
						});
						entryPromises.push(entryPromise);
					}
				}
			}
			return new Promise((resolve, reject) => {
				Promise.all(entryPromises)
				.catch((err) => {
					reject();
					if (errorCallback) { errorCallback(err); }
				})
				.then((rsp: (DirectoryFile | File)[]) => {
					resolve(rsp);
					if (successCallback) {
						successCallback(rsp);
					}
				});
			});
		}

		// Group files by type and filter out whose type that can't be read
		// Param files: should be either directory files OR regular files. Can't be a mix of them.
		// Param FolderName: should be provided if files are from a folder
		static groupFiles(files: File[] | FileList, folderName?, successCallback?, errorCallback?): Promise<GroupedFiles> {
			return new Promise((resolve, reject) => {
				const filesByType: GroupedFiles = {
					txt: {}, // .txt, .csv
					txtZip: {},
					fgdb: {}, // fgdb file
					shp: {}, // shapefiles including .shp, .dbf, .prj
					fgdbZip: {}, // .zip
					shpZip: {} // .zip
				};
				// Is fgdb folder (not including zip)
				let isFgdb;
				const shpExtensions = ['SHP', 'PRJ', 'DBF', 'SHX'];
				const fgdbExtensions = ['GDBTABLE', 'GDBTABLX'];
				const txtExtensions = ['CSV', 'TXT'];
				const fgdbExcludedFiles = ['a00000001', 'a00000002', 'a00000003', 'a00000005', 'a00000006', 'a00000007', 'a00000008'];
				const promises = [];
				for (let i = 0; i < files.length; i++) {
					const file = files[i];
					const nameParts = this.getFileNameParts(file.name);
					const nameNoExt = nameParts.nameNoExt;
					const extension = nameParts.ext;
					switch (extension) {
						case 'TXT':
						case 'CSV':
							// // Use name instead of nameNoExt
							// filesByType.txt[file.name] = {
							// 	file: file
							// };
							filesByType.txt[file.name] = {
								file: file
							};
							break;
						case 'DBF':
						case 'SHP':
						case 'PRJ':
						case 'SHX':
							if (!filesByType.shp[nameNoExt]) {
								filesByType.shp[nameNoExt] = {
									files: {
										SHP: null,
										SHX: null,
										DBF: null,
										PRJ: null
									},
									info: null,
									zip: null
								};
							}
							filesByType.shp[nameNoExt].files[extension] = file;
							break;
						case 'ZIP':
							const promise = new Promise((resolveZ, rejectZ) => {
								this.readZip(file).then((rsp: any) => {
									// Check zip is shp or fgdb or txt
									// const fgdbNames = ['a00000004.gdbtable', 'a00000004.gdbtablx'];
									let zipType;
									for (const f of Object.keys(rsp.files)) {
										// Make sure f is not a directory
										if (rsp.files[f].dir) {
											continue;
										}
										// Make sure f has name
										const splits = f.split('/');
										const fName = splits[splits.length - 1];
										if (!fName) {
											continue;
										}
										const fNameParts = this.getFileNameParts(fName);
										const fExt = fNameParts.ext;
										const fNameNoExt = fNameParts.nameNoExt;
										const checkFunc = (type) => {
											switch (type) {
												case 'shp':
													// Check shp extensions
													const extIndexS = shpExtensions.indexOf(fExt);
													if (extIndexS !== -1) {
														// Add File to filesByType
														if (!filesByType.shpZip[nameNoExt]) {
															filesByType.shpZip[nameNoExt] = {
																zip: file,
																layers: {}
															};
														}
														if (!filesByType.shpZip[nameNoExt].layers[fNameNoExt]) {
															filesByType.shpZip[nameNoExt].layers[fNameNoExt] = {
																unzips: {},
																info: null
															};
														}
														filesByType.shpZip[nameNoExt].layers[fNameNoExt].unzips[fExt] = rsp.files[f];
													}
													break;
												case 'fgdb':
													// File extension need to be in fgdbExtensions
													const extIndexF = fgdbExtensions.indexOf(fExt);
													if (extIndexF === -1) { break; }
													// File name can not be in fgdbExcludedFiles
													const excludeIndexF = fgdbExcludedFiles.indexOf(fNameNoExt);
													if (excludeIndexF !== -1) { break; }
													// Add File to filesByType
													if (!filesByType.fgdbZip[nameNoExt]) {
														filesByType.fgdbZip[nameNoExt] = {
															zip: file,
															unzips: {}
														};
													}
													if (!filesByType.fgdbZip[nameNoExt].unzips[fNameNoExt]) {
														filesByType.fgdbZip[nameNoExt].unzips[fNameNoExt] = {};
													}
													filesByType.fgdbZip[nameNoExt].unzips[fNameNoExt][fExt] = rsp.files[f];
													break;
												case 'txt':
													// File extension need to be in txtExtensions
													const extIndexTxt = txtExtensions.indexOf(fExt);
													if (extIndexTxt === -1 ) { break; }
													// Add File to filesByType
													if (!filesByType.txtZip[nameNoExt]) {
														filesByType.txtZip[nameNoExt] = {
															zip: file,
															layers: {}
														};
													}
													// Use fName instead of nameNoExt here. Since XXX.txt and XXX.csv could be two different layers
													filesByType.txtZip[nameNoExt].layers[fName] = {
														unzip: rsp.files[f]
													};
													break;
												default:
													const extIndexShp = shpExtensions.indexOf(fExt);
													if (extIndexShp !== -1) {
														zipType = 'shp';
														checkFunc(zipType);
													} else if (fgdbExtensions.indexOf(fExt) !== -1) {
														// File extension need to be in fgdbExtensions
														zipType = 'fgdb';
														checkFunc(zipType);
														// // File extension need to be in fgdbExtensions
														// const extIndexFgdb = fgdbExtensions.indexOf(fExt);
														// if (extIndexFgdb === -1) { break; }
														// zipType = 'fgdb';
														// checkFunc(zipType);
													} else if (txtExtensions.indexOf(fExt) !== -1) {
														zipType = 'txt';
														checkFunc(zipType);
													}
											}
										};
										checkFunc(zipType);
									}
									resolveZ();
								}).catch((err) => {
									resolveZ();
								});
							});
							promises.push(promise);
							break;
						case 'GDBTABLE':
						case 'GDBTABLX':
							// If it's unzipped FGDB, the files has to be uploaded from a folder (instead of selecting raw fgdb files directly)
							if (!folderName) { break; }
							// File name can not be in fgdbExcludedFiles
							const excludeIndexF = fgdbExcludedFiles.indexOf(nameNoExt);
							if (excludeIndexF !== -1) { break; }
							isFgdb = true;
							// Add File to filesByType
							if (!filesByType.fgdb[folderName]) {
								filesByType.fgdb[folderName] = {
									zip: null,
									files: {},
									rawFiles: null
								};
							}
							if (!filesByType.fgdb[folderName].files[nameNoExt]) {
								filesByType.fgdb[folderName].files[nameNoExt] = {};
							}
							filesByType.fgdb[folderName].files[nameNoExt][extension] = file;
						// TODO !!!!!!!!!!!!!!!!!
							// // Remove .gdb from name according to Jinzhou
							// const gdbExt = nameSplits[nameSplits.length - 2];
							// if (gdbExt && gdbExt.toUpperCase() === 'GDB') {
							// 	const name = nameNoExt.slice(0, nameNoExt.lastIndexOf('.'));
							// 	filesByType.fgdb[name] = file;
							// }
					}
					if (isFgdb) {
						filesByType.fgdb[folderName].rawFiles = files;
					}
				}
				for (const name of Object.keys(filesByType.fgdb)) {
					if (!this.checkFgdbComplete(filesByType.fgdb[name].files)) {
						delete filesByType.fgdb[name];
						continue;
					}
					// Generate zip file
					const zipPromise = new Promise((resolveZip, rejectZip) => {
						this.generateZip(filesByType.fgdb[name].rawFiles).then((rsp) => {
							filesByType.fgdb[name].zip = rsp;
							resolveZip();
						}).catch(() => {
							rejectZip();
						});
					});
					promises.push(zipPromise);
					// const fgdbFiles = [];
					// for (const fName of Object.keys(filesByType.fgdb[name].files)) {
					// 	for (const ext of Object.keys(filesByType.fgdb[name].files[fName])) {
					// 		fgdbFiles.push(filesByType.fgdb[name].files[fName][ext]);
					// 	}
					// }
					// filesByType.fgdb[name].zip = this.generateZip(fgdbFiles);
				}
				Promise.all(promises).then((rsp) => {
					if (successCallback) {
						successCallback(filesByType);
					}
					resolve(filesByType);
				})
				.catch((err) => {
					if (errorCallback) { errorCallback(err); }
				});
			});
		}
		// Group files which include regular ones and those uploaded from directories
		static groupFilesMix(files: (DirectoryFile | File)[], successCallback?, errorCallback?): Promise<GroupedFiles> {
			return new Promise((resolve, reject) => {
				const promisesGroupedFiles = [];
				const filesNotDir = [];
				files.forEach((data) => {
					// Direcotry files
					if (data.hasOwnProperty('dirName')) {
						//
						const promiseDir = new Promise((resolveDir, rejectDir) => {
							FP.FileUtility.groupFiles((<DirectoryFile>data).files, (<DirectoryFile>data).dirName, (groupedFilesDir) => {
								resolveDir(groupedFilesDir);
							}, () => {
								rejectDir();
							});
						});
						promisesGroupedFiles.push(promiseDir);
					} else { // Regular files
						filesNotDir.push(data);
					}
				});
				const promiseRegular = new Promise ((resolveRegular, rejectRegular) => {
					FP.FileUtility.groupFiles(filesNotDir, null, (groupedFilesReg) => {
						resolveRegular(groupedFilesReg);
					}, (err) => {
						rejectRegular();
					});
				});
				promisesGroupedFiles.push(promiseRegular);
				Promise.all(promisesGroupedFiles)
				.catch((err) => {
					if (errorCallback) { errorCallback(err); }
				})
				.then((rspGroupedFiles: GroupedFiles[]) => {
					const groupedFiles = FP.GeneralUtility.mergeObjectsLevel1(rspGroupedFiles);
					// rspGroupedFiles.reduce((acc, g) => {
					// 	if (g) {
					// 		for (const key of Object.keys(g)) {
					// 			if (!acc[key]) {
					// 				acc[key] = g[key];
					// 			} else {
					// 				for (const subKey of Object.keys(g[key])) {
					// 					acc[key][subKey] = g[key][subKey];
					// 				}
					// 			}
					// 		}
					// 	}
					// 	return acc;
					// });
					resolve(groupedFiles);
					if (successCallback) {
						successCallback(groupedFiles);
					}
				});
			});
		}


		// Process grouped files.
		// Including reading shp / fgdb...
		static processFilesByType(filesByType: GroupedFiles,
			successCallback?,
			errorCallback?,
			options?: FileProcessOptions
		): Promise<GroupedFiles> {
			const promises = [];
			if (filesByType.fgdbZip) {
				for (const name of Object.keys(filesByType.fgdbZip)) {
					const promise = new Promise((resolveFgdb, rejectFgdb) => {
						if (!this.checkFgdbComplete(filesByType.fgdbZip[name].unzips)) {
							resolveFgdb();
						} else {
							this.readFgdbFiles(filesByType.fgdbZip[name].unzips, true).then((rspFgdb) => {
								filesByType.fgdbZip[name].layers = rspFgdb;
								resolveFgdb();
							});
						}
					});
					promises.push(promise);
				}
			}
			if (filesByType.shpZip) {
				for (const name of Object.keys(filesByType.shpZip)) {
					for (const layerName of Object.keys(filesByType.shpZip[name].layers)) {
						const promise = new Promise((resolveShpZip, rejectShpZip) => {
							this.readShapefiles_Z(filesByType.shpZip[name].layers[layerName], options).then((rspShpZip) => {
								filesByType.shpZip[name].layers[layerName].info = rspShpZip;
								resolveShpZip();
							});
						});
						promises.push(promise);
					}
				}
			}
			if (filesByType.shp) {
				for (const name of Object.keys(filesByType.shp)) {
					// Create a zip file
					const files = [];
					for (const f of Object.keys(filesByType.shp[name].files)) {
						files.push(filesByType.shp[name].files[f]);
					}
					const zipPromise = new Promise((resolveZip, rejectZip) => {
						this.generateZip(files).then((rsp) => {
							filesByType.shp[name].zip = rsp;
							resolveZip();
						}).catch(() => {
							rejectZip();
						});
					});
					promises.push(zipPromise);
					// filesByType.shp[name].zip = this.generateZip(files);
					// Read
					const promise = new Promise((resolveShp, rejectShp) => {
						this.readShapefiles_F(filesByType.shp[name].files, name, options).then((rspShp) => {
							filesByType.shp[name].info = rspShp;
							resolveShp();
						}).catch(() => {
							delete filesByType.shp[name];
							resolveShp();
						});
					});
					promises.push(promise);
				}
			}
			if (filesByType.fgdb) {
				for (const name of Object.keys(filesByType.fgdb)) {
					const promise = new Promise((resolveFgdb, rejectFgdb) => {
						this.readFgdbFiles(filesByType.fgdb[name].files)
						.catch(() => { rejectFgdb(); })
						.then((rspFgdb) => {
							filesByType.fgdb[name].layers = rspFgdb;
							resolveFgdb();
						});
					});
					promises.push(promise);
				}
			}
			if (filesByType.txtZip) {
				for (const name of Object.keys(filesByType.txtZip)) {
					for (const layerName of Object.keys(filesByType.txtZip[name].layers)) {
						const promise = new Promise((resolveTxtZip, rejectTxtZip) => {
							this.readTxt_z(filesByType.txtZip[name].layers[layerName].unzip).then((rsp) => {
								filesByType.txtZip[name].layers[layerName].binaryString = rsp.binaryString;
								resolveTxtZip();
							}).catch(() => {
								rejectTxtZip(); // TODO
							});
						});
						promises.push(promise);
					}
				}
			}
			if (filesByType.txt) {
				for (const name of Object.keys(filesByType.txt)) {
					const zipPromise = new Promise((resolveZip, rejectZip) => {
						this.generateZip([filesByType.txt[name].file]).then((rsp) => {
							filesByType.txt[name].zip = rsp;
							resolveZip();
						}).catch(() => {
							rejectZip();
						});
					});
					promises.push(zipPromise);
					const promise = new Promise((resolveTxt, rejectTxt) => {
						const reader: FileReader = new FileReader();
						reader.onload = () => {
							filesByType.txt[name].binaryString = reader.result;
							resolveTxt();
						};
						reader.readAsText(filesByType.txt[name].file);						
						// reader.readAsText(filesByType.txt[name].file, 'UTF-8');
						// reader.readAsText(filesByType.txt[name].file, 'IBM866');
						// reader.readAsText(filesByType.txt[name].file, 'ISO-8859-2');
						// reader.readAsText(filesByType.txt[name].file, 'ISO-8859-3');
						// https://stackoverflow.com/a/9346865/7585561
						// reader.readAsBinaryString(filesByType.txt[name].file);
					});
					promises.push(promise);
				}
			}
			return Promise.all(promises)
				.catch((err) => {
					if (errorCallback) { errorCallback(err); }
				}).then(() => {
					try {
						if (successCallback) { successCallback(filesByType); }
					} catch (err) {
						if (errorCallback) { errorCallback(err); }
					}
					return filesByType;
				});
		}

		// Read zip files
		static readZip(file: File, successCallback?, errorCallback?, options?: JSZip.JSZipLoadOptions): Promise<JSZip> {
			return new Promise((resolve, reject) => {
				const zip = new JSZip();
				zip.loadAsync(file, options)
					.then((rsp) => {
						resolve(rsp);
						if (successCallback) {
							successCallback(rsp);
						}
					}).catch((err) => {
						reject(err);
						if (errorCallback) {
							errorCallback(err);
						}
					});
			});
		}
		// Read fgdb files
		// isJSZip: is files to read unziped from a .zip or regular files
		static readFgdbFiles(files, isJSZip?, successCallback?, errorCallback?): Promise<any> {
			return new Promise((resolve, reject) => {
				const fgdb = files;

				const getPromise = (fileToRead) => {
					let promiseFileToRead;
					if (isJSZip) {
						promiseFileToRead = fileToRead.async('arraybuffer');
					} else {
						promiseFileToRead = new Promise((resolveFileToRead, rejectFileToRead) => {
							const reader: FileReader = new FileReader();
							reader.onload = () => {
								resolveFileToRead(reader.result);
							};
							reader.readAsArrayBuffer(fileToRead);
						});
					}
					return promiseFileToRead;
				};

				// Read a00000004 file before reading other files
				getPromise(fgdb['a00000004'].GDBTABLX).then((rsp04Tablx) => {
					const results = RE.Fgdb.interpretGdbTablx(rsp04Tablx);
					// Read layers info
					// fgdb['a00000004'].GDBTABLE.async('arraybuffer').then((rsp04Table) => {
					getPromise(fgdb['a00000004'].GDBTABLE).then((rsp04Table) => {
						const results04Table = RE.Fgdb.interpretGdbTable(rsp04Table, results.rowStartPos, true);
						// Start reading other files
						const promisesLayers = [];
						for (const name of Object.keys(fgdb)) {
							if (name === 'a00000004') {
								continue;
							}
							const file = fgdb[name];
							if (!file.GDBTABLE || !file.GDBTABLX) {
								continue;
							}
							const layerPromise = new Promise((resolveL, rejectL) => {
								getPromise(file.GDBTABLX).then((rspLayerTablx) => {
									const resultLayerTablx = RE.Fgdb.interpretGdbTablx(rspLayerTablx);
									getPromise(file.GDBTABLE).then((rspLayerTable) => {
										const resultLayerTable: any = RE.Fgdb.interpretGdbTable(rspLayerTable, resultLayerTablx.rowStartPos, 20);
										resultLayerTable.fileName = name;
										resultLayerTable.fields.forEach((field) => {
											const convertedType = FP.FileSpec.convertFieldTypeFgdb(field.type);
											Object.assign(field, convertedType);
										});
										resolveL(resultLayerTable);
									});
								});
							});
							promisesLayers.push(layerPromise);
						}
						Promise.all(promisesLayers).then((rspLayerContents) => {
							const layers = this.fgdbMatchLayerAndCols(results04Table.rows, rspLayerContents);
							if (successCallback) { successCallback(layers); }
							resolve(layers);
						});
					});
				});
			});
		}
		// Match layers (read from a00000004) with columns (read from a00000009/a/b..) in a FGDB
		static fgdbMatchLayerAndCols(layers, contents) {
			// Filter layers
			layers = layers.slice(2, layers.length); // The first record is reserved for a kind of root item ( Name = "", Path = "" ). The second record is reserved for a Name = "Workspace" item, Path = ""
			layers = layers.filter((l) => {
				return !!l.Path && l.DatasetInfo1;
			});
			// Filter contents
			contents = contents.filter((c) => {
				return !!c.geomType;
			});
			//
			const layersByName = {};
			for (const c of contents) {
				let orderStr = c.fileName.split('.')[0];
				if (orderStr) {
					orderStr = orderStr.substr(orderStr.length - 3);
					c.order = parseInt(orderStr, 16);
				}
			}
			// Sort contents by name
			contents.sort((a, b) => {
				return a.order - b.order;
			});
			for (let i = 0, j = 0; i < layers.length; i++) {
				// if (!layers[i].Path) {
				// 	continue;
				// }
				contents[j].layerName = layers[i].Name;
				contents[j].type = contents[j].geomType;
				delete contents[j].geomType; // TODO
				layersByName[layers[i].Name] = contents[j];
				// Get projection from layer definition xml
				let epsgNum;
				const reg = /(epsg)(\D*)(\d+)/i;
				const match = layers[i].Definition.match(reg);
				epsgNum = match ? match[3] : null;
				if (epsgNum) {
					layersByName[layers[i].Name].epsg = epsgNum;
				}
				j++;
			}
			return layersByName;
		}

		// Read Shapefiles unziped from a .zip
		static readShapefiles_Z(files, successCallback?, errorCallback?, options?: FileProcessOptions): Promise<any> {
			return new Promise((resolve, reject) => {
				const shapefile = files;
				// Read DBF
				shapefile.unzips.DBF.async('arraybuffer').then((rspDbf) => {
					let shpOptions: RE.ShapefileOptions;
					if (options) {
						shpOptions = {};
						shpOptions.previewNum = options.previewNumShp;
					}
					const resultsDbf = RE.Shapefile.interpretDbf(rspDbf, shpOptions);
					const partsPromises = [];
					// Read PRJ
					const promisePrj: Promise<any> = new Promise((resolvePrj, rejectPrj) => {
							// file.epsgOrigin = this.getEpsgFromWktPrj(reader.result);
						shapefile.unzips.PRJ.async('text').then((rspPrj) => {
							const resultPrj = RE.Shapefile.interpretPrj(rspPrj);
							resolvePrj(resultPrj);
						});
					});
					partsPromises.push(promisePrj);
					// Read Shp
					const promiseShp: Promise<any> = new Promise((resolveShp, rejectShp) => {
						shapefile.unzips.SHP.async('arraybuffer').then((rspShp) => {
							const resultShp = RE.Shapefile.interpretShp(rspShp);
							resolveShp(resultShp);
						});
					});
					partsPromises.push(promiseShp);
					Promise.all(partsPromises)
						.then((rsp) => {
							resolve({
								fields: resultsDbf.headers.map((h) => {
									const convertedType = FP.FileSpec.convertFieldTypeDbf(h.type);
									const field = {
										name: h.name,
										type: h.type,
										length: h.length
									};
									return Object.assign(field, convertedType);
								}),
								epsg: rsp[0] ? rsp[0].epsg : null,
								type: rsp[1] ? rsp[1].type : null,
								rows: resultsDbf.rows
							});
						})
						.catch((e) => {
							reject();
						});
				});
			});
		}
		// Read shapefiles from folder
		static readShapefiles_F(files, name, options?: FileProcessOptions) {
			return new Promise((resolve, reject) => {

				const shapefilesByName = files;
				// Check missing files
				if (!this.checkShpComplete(files, name)) {
					resolve(null);
					return;
				}
				// Read DBF
				let shpOptions: RE.ShapefileOptions;
				if (options) {
					shpOptions = {};
					shpOptions.previewNum = options.previewNumShp;
				}
				RE.Shapefile.readDbf(shapefilesByName.DBF, shpOptions)
				.then((resultsDbf) => {
					const partsPromises = [];
					// Read PRJ
					const promisePrj: Promise<any> = new Promise((resolvePrj, rejectPrj) => {
						RE.Shapefile.readPrj(shapefilesByName.PRJ).then((resultsPrj) => {
							resolvePrj(resultsPrj);
						});
					});
					partsPromises.push(promisePrj);
					// Read Shp
					const promiseShp: Promise<any> = new Promise((resolveShp, rejectShp) => {
						RE.Shapefile.readShp(shapefilesByName.SHP).then((resultsShp) => {
							resolveShp(resultsShp);
						});
					});
					partsPromises.push(promiseShp);
					Promise.all(partsPromises)
						.then((rsp) => {
							resolve({
								fields: resultsDbf.headers.map((h) => {
									const convertedType = FP.FileSpec.convertFieldTypeDbf(h.type);
									const field = {
										name: h.name,
										type: h.type,
										length: h.length
									};
									return Object.assign(field, convertedType);
								}),
								epsg: rsp[0] ? rsp[0].epsg : null,
								type: rsp[1] ? rsp[1].type : null,
								rows: resultsDbf.rows
							});
						})
						.catch();
				});
			});
		}
		// Read txt file unziped from a .zip
		static readTxt_z(file: JSZip.JSZipObject, eventColumnSeparator?: string, previewRowNum?): Promise<{binaryString: string}> {
			return new Promise((resolve, reject) => {
				if (!previewRowNum && previewRowNum !== 0) {
					previewRowNum = 10;
				}
				file.async('binarystring').then((rsp) => {
					resolve({
						binaryString: rsp
					});
				}).catch(() => {
					resolve({ binaryString: ''});
				});
			});
		}
		// Check if shapefile contains all the required files
		static checkShpComplete(shapefile, name): boolean {
			if (!shapefile.DBF) {
				alert('Missing dbf file of ' + name);
				return false;
			} else if (!shapefile.SHP) {
				alert('Missing shp file of ' + name);
				return false;
			} else if (!shapefile.PRJ) {
				alert('Missing prj file of ' + name);
				return false;
			} else if (!shapefile.SHX) {
				alert('Missing shx file of ' + name);
				return false;
			}
			return true;
		}
		// Check if fgdb contains all the required files
		static checkFgdbComplete(fgdb): boolean {
			if (!fgdb['a00000004']) {
				alert('Missing a00000004 file!');
				return false;
			}
			if (!fgdb['a00000004'].GDBTABLX) {
				alert('Missing a00000004.gdbtablx file!');
				return false;
			}
			if (!fgdb['a00000004'].GDBTABLE) {
				alert('Missing a00000004.gdbtable file!');
				return false;
			}
			return true;
		}

		// Create a zip file
		static generateZip(files: File[] | FileList): Promise<Blob> {
			return new Promise((resolve, reject) => {
				const zip = new JSZip();
				for (let i = 0; i < files.length; i++) {
					zip.file(files[i].name, files[i]);
				}
				zip.generateAsync({type: 'blob'}).then((content) => {
					resolve(content);
				}).catch((err) => {
					reject();
				});
			});
		}
	}

	export class GeneralUtility {
		// Merge two objects by first level of properties
		// E.g. objA = {A: {AA: 1}, B: {AB: 1}}  objB = {A: {BB: 1}, B: {BB: 2}}
		// Result would be {A: {AA: 1, BB: 1}, B: {AB: 1, BB: 2}}
		static mergeObjectsLevel1(objectsArray: object[]) {
			const mergedObj = objectsArray.reduce((acc, obj) => {
				if (obj) {
					for (const key of Object.keys(obj)) {
						if (!acc[key]) {
							acc[key] = obj[key];
						} else {
							for (const subKey of Object.keys(obj[key])) {
								acc[key][subKey] = obj[key][subKey];
							}
						}
					}
				}
				return acc;
			});
			return mergedObj;
		}
	}

	export class FileSpec {
		// lrsCols, attrCols: Default true
		static initLayerJson(lrsCols?, attrCols?): LayerJsonSpatial {
			const fileJson: LayerJsonSpatial = {
				name: '',
				type: '', // feature type such as 'GA', 'GP', 'GLM'...
				sequence: null,
				layer_spec: null
			};
			// Default: have lrsCols and attrCols
			if (lrsCols || lrsCols === undefined) {
				fileJson.lrs_cols = [];
			}
			if (attrCols || attrCols === undefined) {
				fileJson.attr_cols = [];
			}
			return fileJson;
		}
		static initLayerJsonCsv(name: string, nameWithExt, type, delimiter: string, lrsCols?, attrCols?): LayerJsonCsv {
			return {
				name: name,
				originalName: nameWithExt,
				type: type,
				sequence: null,
				layer_spec: {
					delimiter: delimiter
				},
				lrs_cols: lrsCols || null,
				attr_cols: attrCols || null
			};
		}
		static initFilesJsonSpatial(): FilesJsonSpatial {
			return {
				name: '',
				type: null,
				layers: []
			};
		}
		static initFilesJsonCsv(name: string, layers?: LayerJsonCsv[]): FilesJsonCsv {
			return {
				name: name,
				type: 'csv',
				layers: layers || []
			};
		}
		static convertFeatureType(featureType) {
			switch (featureType) {
				case 'Point':
				case 'MultiPoint':
				case 'PointZ':
				case 'MultiPointZ':
					return 'GP';
				case 'PointM':
				case 'MultiPointM':
					return 'GPM';
				case 'PolyLine':
					return 'GL';
				case 'PolyLineM':
					return 'GLM';
				case 'PolyLineZ':
				 // TODO!!!!!!!!!
				case 'Polygon':
					return 'GA';
			}
		}
		static getFeatureFileJsonFgdb(fgdb, name) {
			const filesJson = {
					name: name,
					layers: []
			};
			// fgdb.forEach((l) => {
			// 	const layerJson: any = {};
			// 	layerJson.source = l.name;
			// 	layerJson.name = l.name;
			// 	layerJson.type = l.featureTypeShp;
			// 	layerJson.file_spec = {
			// 		type: 'fgdb',
			// 		srid: l.epsgOrigin || l.epsgInput,
			// 		proj4: null,
			// 		geom_col: 'geom'
			// 	};
			// 	layerJson.arnold = l.featureType === 'arnold' ? 1 : 0;
			// 	const columnsInfo = this.getFeatureColumnsInfo(l.matchingColumns);
			// 	const cols = this.setFeatureFileLrsAttrCols(columnsInfo, l.headers);
			// 	layerJson.lrs_cols = cols.lrs;
			// 	layerJson.attr_cols = cols.attr;
			// 	filesJson.layers.push(layerJson);
			// });
			return filesJson;
		}

		// Convert the field type read from dbf to LB Type and NENA Type
		static convertFieldTypeDbf(fieldType) {
			switch (fieldType) {
				case 'Y':
				case 'N':
				case 'F':
				case 'B':
				case 'I':
					return {
						LBType: 'number',
						NenaType: 'Long'
					};
				case 'C':
				case 'M':
					return {
						LBType: 'text',
						NenaType: 'text'
					};
				case 'D':
					return {
						LBType: 'date',
						NenaType: 'date'
					};
				case 'L':
					return {
						LBType: 'bool',
						NenaType: ''
					};
				default:
					return {
						LBType: '',
						NenaType: ''
					};
			}
		}
		// Convert the field type read from fgdb to LB Type and NENA Type
		static convertFieldTypeFgdb(fieldType) {
			switch (fieldType) {
				case 0:
				case 1:
				case 6:
					return {
						LBType: 'number',
						NenaType: 'long'
					};
				case 4:
				case 10:
				case 11:
				case 12:
					return {
						LBType: 'text',
						NenaType: 'text'
					};
				case 5:
					return {
						LBType: 'date',
						NenaType: 'date'
					};
				case 7:
					return {
						LBType: 'geom',
						NenaType: ''
					};
				case 8:
					return {
						LBType: 'bool',
						NenaType: ''
					};
				case 2:
				case 3:
					return {
						LBType: 'number',
						NenaType: 'float'
					};
				default:
					return {
						LBType: '',
						NenaType: ''
					};
			}
		}

		// TXT: get layers by a column value
		static getTxtLayersByColumn(fileData: string[], delimiter: string, col?: string): LayersByName {
			const layersByName: LayersByName = {};
			if (!fileData || !fileData[0]) { return; }
			// Default column: 'Data_Item'
			col = col || 'Data_Item';
			// Get headers from first line
			const headers = fileData[0].split(delimiter);
			// Layer name column index in headers
			const colIdx = headers.indexOf(col);
			if (colIdx !== -1) {
				// Loop fileData from second line
				for (let i = 1; i < fileData.length; i++) {
					if (!fileData[i]) { continue; }
					// Layer name
					const name = fileData[i].split(delimiter)[colIdx];
					// Create layer if not existing
					if (!layersByName[name]) {
						layersByName[name] = {
							fileData: [fileData[0]],
							name: name
						};
					}
					// Push to fileData
					layersByName[name].fileData.push(fileData[i]);
				}
			}
			return layersByName;
		}
		// Return the whole file data as one layer in layersByName format
		static getTxtLayers(fileName: string, fileData: any[]): LayersByName {
			const layersByName = {};
			const nameNoExt = FileUtility.getFileNameParts(fileName).nameNoExt;
			layersByName[nameNoExt] = {
				name: nameNoExt,
				fileData: fileData
			};
			return layersByName;
		}
	}

}



// WEBPACK FOOTER //
// ./src/assets/TS/FileProcess.ts